<template>
    <div class="create-factory">
      <div class="create-factory-container">
        <div class="mt-2">
            <div class="input-group imp" data-validation="name">
                <label class="key">Factory Name:</label>
                <input v-model="newFactory.name" />
            </div>
        </div>
        <div class="mt-3">
            <div class="input-group" data-validation="supervisor">
                <label class="key">Supervisor:<span class="required-asterisk">*</span></label>
                <MultiSelectDropdown
                    :options="filteredSupervisors"
                    @input="handleSupervisorSelection"
                    :dataValidation="'supervisor'"
                    :selected="selectedSupervisors"
                    :enableSearch="true"
                    placeholder="Select Supervisors"
                />
            </div>
        </div>
        <div class="grid-2 mt-2">
            <div class="input-group imp" data-validation="city">
                <label class="key">City:</label>
                <input v-model="newFactory.city" />
            </div>
            <div class="input-group imp" data-validation="state">
                <label class="key">Region / State:</label>
                <input v-model="newFactory.state" />
            </div>
        </div>
        <div class="mt-2">
            <div class="input-group imp" data-validation="address">
                <label class="key">Address:</label>
                <textarea v-model="newFactory.address" />
            </div>
        </div>
        <div class="mt-2">
            <div class="input-group imp" data-validation="description">
                <label class="key">Description:</label>
                <textarea v-model="newFactory.description" />
            </div>
        </div>
      </div>
        <div class="flex flex-end mt-3 s">
            <button class="btn btn-black mr-3 pointer" @click="cancel">Cancel</button>
            <button class="btn pointer" :disabled="disableSave" @click="SaveFactory"> {{ newFactory.id ? 'Update' :
                'Save' }}</button>
        </div>
    </div>
</template>

<script>
import { AddSupervisorToFactory, CreateFactory, RemoveUserFromFactory, UpdateFactory } from '@/api'
import MultiSelectDropdown from '@/components/common/customMultiSelect.vue'
import { alert, success } from '@/plugins/notification'
import { emptyString, emptylist } from '@/helper/formValidation'
import Config from '@/config.js'
import { mapGetters } from 'vuex'
export default {
  name: 'createFactory',
  components: {
    MultiSelectDropdown
  },
  data () {
    return {
      disableSave: false,
      newFactory: {
        name: '',
        city: '',
        state: '',
        address: '',
        description: ''
      },
      selectedSupervisors: [],
      previouslySelectedSupervisors: [],
      supervisorsSearchKeyword: '',
      usersForSupervisors: [],
      loadingSupervisors: false,
      otherUsersinFactory: []
    }
  },
  computed: {
    ...mapGetters(['tenantUsersList']),
    filteredSupervisors () {
      if (this.supervisorsSearchKeyword.trim() === '') {
        return this.usersForSupervisors
      }
      return this.usersForSupervisors.filter(user =>
        user.name.toLowerCase().includes(this.supervisorsSearchKeyword.toLowerCase())
      )
    }
  },
  props: {
    factoryData: {
      type: Object,
      default: () => ({})
    },
    action: {
      type: String,
      default: 'create'
    }
  },
  mounted () {
    if (this.action === 'edit' && this.factoryData) {
      this.newFactory.id = this.factoryData.id
      this.newFactory.name = this.factoryData.name
      this.newFactory.city = this.factoryData?.address?.city || ''
      this.newFactory.state = this.factoryData?.address?.state || ''
      this.newFactory.address = this.factoryData?.address?.address || ''
      this.newFactory.description = this.factoryData.description
      for (const assoc of this.factoryData.project_user_associations) {
        if (assoc.role_id === Config.USER_ROLE_MAP.SUPERVISOR) {
          this.selectedSupervisors.push(assoc.user_id)
        } else {
          this.otherUsersinFactory.push(assoc.user_id)
        }
      }
      this.previouslySelectedSupervisors = [...this.selectedSupervisors]
    }
    for (const user of this.tenantUsersList) {
      if (user.status === Config.USER_STATUS_MAP.ACTIVE ||
      (user.status === Config.USER_STATUS_MAP.INVITED && user?.associated_user.status === Config.USER_STATUS_MAP.ACTIVE)) {
        let disabled = user.associated_role?.id === Config.USER_ROLE_MAP.ADMIN
        for (const item of this.otherUsersinFactory) {
          if (item === user.associated_user.id) {
            disabled = true
            break
          }
        }
        this.usersForSupervisors.push({
          id: user.associated_user.id,
          name: `${user.associated_user.first_name} ${user.associated_user.last_name}`,
          disabled
        })
      }
    }
  },
  methods: {
    cancel () {
      this.$emit('cancel')
    },
    validateInput () {
      let valid = true
      if (!this.newFactory.name.trim()) {
        emptyString(this.newFactory.name.trim(), 'name', 'name', false)
        valid = false
      }
      if (!this.newFactory.city.trim()) {
        emptyString(this.newFactory.city.trim(), 'city', 'city', false)
        valid = false
      }
      if (!this.newFactory.state.trim()) {
        emptyString(this.newFactory.state.trim(), 'state', 'state', false)
        valid = false
      }
      if (!this.newFactory.address.trim()) {
        emptyString(this.newFactory.address.trim(), 'address', 'address', false)
        valid = false
      }
      if (!this.newFactory.description.trim()) {
        emptyString(this.newFactory.description.trim(), 'description', 'description', false)
        valid = false
      }
      if (!this.selectedSupervisors.length) {
        emptylist(this.selectedSupervisors, 'supervisor', 'supervisor')
        valid = false
      } else if (!valid) {
        alert('Please fill all the required fields')
      }
      return valid
    },
    updateFactory () {
      if (this.disableSave) return
      if (!this.validateInput()) return
      this.disableSave = true
      const body = {
        name: this.newFactory.name.trim(),
        description: this.newFactory.description.trim(),
        address: {
          state: this.newFactory.state.trim(),
          city: this.newFactory.city.trim(),
          address: this.newFactory.address.trim()
        }
      }
      const usersToBeAddedAsSupervisors = []
      const usersToBeRemoved = []
      for (const user of this.selectedSupervisors) {
        if (!this.previouslySelectedSupervisors.includes(user)) {
          usersToBeAddedAsSupervisors.push({
            user_id: user,
            role_id: Config.USER_ROLE_MAP.SUPERVISOR,
            project_id: this.factoryData.id
          })
        }
      }
      for (const user of this.previouslySelectedSupervisors) {
        if (!this.selectedSupervisors.includes(user)) {
          usersToBeRemoved.push(user)
        }
      }
      UpdateFactory(body, this.newFactory.id).then(() => {
        if (usersToBeAddedAsSupervisors.length > 0 || usersToBeRemoved.length > 0) {
          const promises = []
          if (usersToBeRemoved.length > 0) {
            const useFactoryToken = false
            promises.push(RemoveUserFromFactory(usersToBeRemoved, useFactoryToken, this.factoryData.id))
          }
          if (usersToBeAddedAsSupervisors.length > 0) {
            promises.push(AddSupervisorToFactory(usersToBeAddedAsSupervisors))
          }
          Promise.all(promises).then(() => {
            success('Factory updated successfully')
            this.$emit('new-factory-created')
          }).catch(() => {
            alert('Error updating supervisors for the factory')
          })
        } else {
          success('Factory updated successfully')
          this.$emit('new-factory-created')
        }
      }).catch((err) => {
        if (err[0]?.message.includes('Uniqueness violation')) {
          this.$notify.alert('Factory name already exists')
        } else {
          this.$notify.alert(err?.message ?? 'Something went wrong')
        }
      }).finally(() => {
        this.disableSave = false
      })
    },
    addFactory () {
      if (this.disableSave) return
      if (!this.validateInput()) return
      this.disableSave = true
      const body = {
        name: this.newFactory.name.trim(),
        description: this.newFactory.description.trim(),
        address: {
          state: this.newFactory.state.trim(),
          city: this.newFactory.city.trim(),
          address: this.newFactory.address.trim()
        },
        is_factory: true,
        project_user_associations: { data: [] }
      }
      body.project_user_associations.data = this.selectedSupervisors.map((supervisor) => {
        return {
          user_id: supervisor,
          role_id: Config.USER_ROLE_MAP.SUPERVISOR
        }
      })
      CreateFactory(body).then(() => {
        success('Factory created successfully')
        this.$emit('new-factory-created')
      }).catch((err) => {
        if (err?.message?.includes('Uniqueness violation')) {
          this.$notify.alert('Factory name already exists')
        } else {
          this.$notify.alert(err?.message ?? 'Something went wrong')
        }
      }).finally(() => {
        this.disableSave = false
      })
    },
    SaveFactory () {
      if (this.newFactory.id) {
        this.updateFactory()
      } else {
        this.addFactory()
      }
    },
    handleSupervisorSelection (supervisor) {
      this.selectedSupervisors = supervisor
    }
  }
}
</script>

<style lang="scss" scoped>
.create-factory {
  max-width: 454px;
  &-container {
    max-height: 60vh;
    padding-left: 10px;
    padding-right: 10px;
    overflow-y: auto;
  }
    textarea {
        resize: none;
    }
}
.required-asterisk {
  color: var(--brand-color-1);
  font-size: 1.2em;
  margin-left: 0.1em;
}
</style>
